import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';

import {
  selectSellVehiclesPinnedFilters,
  togglePinnedFiltersState,
} from '@store';
import { useHasFilters } from 'hooks/common';
import { PUBLISH_UNPUBLISH_OPTIONS } from './constants';

const parseQueryValue = (value) => {
  if (!value) return [];
  if (typeof value === 'string') {
    return value.split(',').filter(Boolean);
  }
  return Array.isArray(value) ? value : [];
};

const getFilterValue = (queryParam, data) =>
  queryParam
    ?.split(',')
    ?.map((item) =>
      data.find((option) => option?.value?.toString() === item.toString())
    ) || [];

export const useSellVehiclesFilterConfig = ({ enums = {} }) => {
  const router = useRouter();
  const pinnedFilters = useSelector(selectSellVehiclesPinnedFilters);
  const dispatch = useDispatch();

  const { brands, categories } = enums;

  const brandFilterValue = brands?.length
    ? getFilterValue(router.query.brand, brands || [])
    : [];

  const models = brandFilterValue?.reduce((acc, cur) => {
    acc = [...acc, ...(cur.models ?? [])];
    return acc;
  }, []);

  useEffect(() => {
    if (!router.query.brand && router.query.model) {
      delete router.query.model;
      router.push(router);
    }

    // When models on url do not match with the selected brands
    if (router.query.brand && router.query.model) {
      const modelBrands = router.query.model.split(',');
      const matchedModels = modelBrands.reduce((acc, cur) => {
        if (
          brandFilterValue?.find((brand) =>
            brand.models.find((_m) => cur === _m.value)
          )
        ) {
          acc.push(cur);
        }
        return acc;
      }, []);
      if (
        enums?.brands?.length &&
        matchedModels.length !== modelBrands.length
      ) {
        router.query.model = matchedModels.map((item) => item).join(',');

        router.push(router);
      }
    }
  }, [router.query, enums?.brands]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterCategories = useMemo(
    () => [
      {
        id: 'brand',
        label: 'Brand',
        icon: PanoramaFishEyeIcon,
        options: brands,
        multiSelect: true,
      },
      {
        id: 'model',
        label: 'Model',
        icon: PanoramaFishEyeIcon,
        options: models,
        multiSelect: true,
      },
      {
        id: 'category',
        label: 'Category',
        icon: PanoramaFishEyeIcon,
        options: categories,
        multiSelect: true,
      },
      {
        id: 'status',
        label: 'Status',
        icon: PanoramaFishEyeIcon,
        options: PUBLISH_UNPUBLISH_OPTIONS,
        multiSelect: false,
      },
    ],
    [brands, categories, models]
  );
  const { hasFilters } = useHasFilters({ pinnedFilters });

  const currentFilters = useMemo(() => {
    return {
      brand: parseQueryValue(router.query.brand),
      model: parseQueryValue(router.query.model),
      category: parseQueryValue(router.query.category),
      status: parseQueryValue(router.query.status),
    };
  }, [router.query]);

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'sellVehicles', pinnedFilter }));
  };

  return {
    filterCategories,
    currentFilters,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
    handlePinnedFilterSelect,
  };
};
